import { SensorResponse } from "@/api/v1/sensor-api";
import { useUpdateSensorQuery } from "@/api/v0/sensors";
import React, { useState } from "react";
import { Marker, MarkerDragEvent } from "react-map-gl/maplibre";
import CircleLayer from "./cirlce-layer";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type Props = {
  sensors?: SensorResponse[];
};

export const SensorsLayer = ({ sensors }: Props) => {
  const updateSensor = useUpdateSensorQuery();
  const [draggedSensor, setDraggedSensor] = useState<{
    sensor: SensorResponse;
    event: MarkerDragEvent;
  } | null>(null);

  const handleDragEnd = async (sensor: SensorResponse, event: MarkerDragEvent) => {
    setDraggedSensor({ sensor, event });
  };

  const handleConfirmUpdate = async () => {
    if (!draggedSensor) return;

    try {
      const { lng: longitude, lat: latitude } = draggedSensor.event.lngLat;
      // Note: Using v0 update mutation until v1 update is implemented
      await updateSensor.mutateAsync({
        sensorId: draggedSensor.sensor.slug || draggedSensor.sensor.organisation_id.toString(),
        latitude: latitude || 0,
        longitude: longitude || 0,
      });
      toast.success("Sensor position updated successfully");
    } catch {
      toast.error("Failed to update sensor position");
    } finally {
      setDraggedSensor(null);
    }
  };

  return (
    <>
      {sensors?.map((sensor) => {
        if (!sensor.latitude || !sensor.longitude) return null;
        const sensorId = sensor.slug || sensor.organisation_id.toString();
        return (
        <React.Fragment key={sensorId}>
          <Marker
            latitude={sensor.latitude}
            longitude={sensor.longitude}
            anchor="center"
            draggable={true}
            onDragEnd={(e) => handleDragEnd(sensor, e)}
            className="cursor-pointer"
            onClick={() => {
              toast.info(`Sensor ${sensor.name}`);
            }}
          >
            <svg height="100" width="100" xmlns="http://www.w3.org/2000/svg">
              <circle
                r="20"
                cx="50"
                cy="50"
                fill="#25db18"
                className="animate-pulse"
              />
              <circle
                r="20"
                cx="50"
                cy="50"
                className="animate-ping origin-center"
                fill="none"
                stroke="#96fa8e"
                strokeWidth="6"
              />
            </svg>
          </Marker>
          <CircleLayer sensor={sensor} />
        </React.Fragment>
        );
      })}

      <AlertDialog
        open={!!draggedSensor}
        onOpenChange={() => setDraggedSensor(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Position Update</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to update the position of sensor{" "}
              {draggedSensor?.sensor.name}? This will change its location
              on the map.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmUpdate}>
              Update Position
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
